<template>
	<div class="container-fluid p-4">
		<div class="d-flex justify-content-between align-items-center mb-4">
			<h1 class="mb-0">Notifications</h1>
			<ButtonLoading
				text="Save"
				icon="bi bi-save"
				:loading="saving"
				:disabled="loading"
				@click="save"
			/>
		</div>

		<div v-if="error" class="alert alert-danger" role="alert">
			{{ error }}
		</div>

		<div v-if="loading" class="text-center p-5">
			<div class="spinner-border text-primary" role="status">
				<span class="visually-hidden">Loading…</span>
			</div>
		</div>

		<template v-else>
			<div class="card mb-4">
				<div class="card-header">Contact Information</div>
				<div class="card-body">
					<div class="row g-3">
						<div class="col-md-6">
							<label class="form-label" for="notification-email">
								Email address
							</label>
							<input
								id="notification-email"
								v-model="settings.email"
								type="email"
								class="form-control"
								placeholder="you@example.com"
								autocomplete="email"
							/>
						</div>
						<div class="col-md-6">
							<label class="form-label" for="notification-phone">
								Phone number
							</label>
							<input
								id="notification-phone"
								v-model="settings.phone"
								type="tel"
								class="form-control"
								placeholder="+1 555 555 5555"
								autocomplete="tel"
							/>
							<div class="form-text">
								Used for SMS notifications. Leave blank to disable SMS.
							</div>
						</div>
					</div>
				</div>
			</div>

			<div class="card">
				<div class="card-header">Channels</div>
				<div class="card-body p-0">
					<table class="table table-hover align-middle mb-0">
						<thead>
							<tr>
								<th scope="col">Channel</th>
								<th scope="col" class="text-center" style="width: 120px">
									Email
								</th>
								<th scope="col" class="text-center" style="width: 120px">
									SMS
								</th>
							</tr>
						</thead>
						<tbody>
							<tr v-for="channel in settings.channels" :key="channel.key">
								<td>
									<div class="fw-semibold">{{ channel.name }}</div>
								</td>
								<td class="text-center">
									<div class="form-check form-switch d-inline-block">
										<input
											:id="`${channel.key}-email`"
											v-model="channel.email"
											class="form-check-input"
											type="checkbox"
											role="switch"
										/>
									</div>
								</td>
								<td class="text-center">
									<div class="form-check form-switch d-inline-block">
										<input
											:id="`${channel.key}-sms`"
											v-model="channel.sms"
											class="form-check-input"
											type="checkbox"
											role="switch"
											:disabled="!settings.phone"
										/>
									</div>
								</td>
							</tr>
							<tr v-if="settings.channels.length === 0">
								<td colspan="3" class="text-center text-muted py-4">
									No notification channels are available.
								</td>
							</tr>
						</tbody>
					</table>
				</div>
			</div>
		</template>

		<ToastNotification
			title="Saved"
			message="Notification settings updated."
			:show="saved"
		/>
	</div>
</template>

<script setup lang="ts">
import { onMounted, ref } from "vue";
import { apiClient } from "@/client";
import ButtonLoading from "@/components/common/ButtonLoading.vue";
import ToastNotification from "@/components/ToastNotification.vue";

interface ChannelSetting {
	key: string;
	name: string;
	email: boolean;
	sms: boolean;
}

interface NotificationSettings {
	email: string;
	phone: string;
	channels: ChannelSetting[];
}

const loading = ref(true);
const saving = ref(false);
const error = ref("");
const saved = ref(false);
const settings = ref<NotificationSettings>({
	email: "",
	phone: "",
	channels: [],
});

async function load() {
	loading.value = true;
	error.value = "";
	try {
		settings.value = await apiClient.JSONGet<NotificationSettings>(
			"/api/notification/settings",
		);
	} catch (e: any) {
		error.value = e?.message || "Failed to load notification settings.";
	} finally {
		loading.value = false;
	}
}

async function save() {
	saving.value = true;
	error.value = "";
	saved.value = false;
	try {
		settings.value = await apiClient.JSONPut<NotificationSettings>(
			"/api/notification/settings",
			settings.value,
		);
		saved.value = true;
	} catch (e: any) {
		error.value =
			e?.response?.data?.message ||
			e?.message ||
			"Failed to save notification settings.";
	} finally {
		saving.value = false;
	}
}

onMounted(load);
</script>
